/**
 * @fileoverview Syndicator Showdown — My Stable card status/timing/earnings helpers
 * @module utils/stableStatus
 *
 * Pure, dependency-free helpers shared by MyStableNew.jsx (and unit-tested
 * in isolation, since the page component itself pulls in Firebase/router).
 */

import { formatMinuteCountdown } from './timezones';

export function toMs(val) {
  if (!val) return null;
  if (typeof val === 'number') return val;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (val._seconds !== undefined) return val._seconds * 1000;
  const ms = new Date(val).getTime();
  return isNaN(ms) ? null : ms;
}

/**
 * Classifies a player_entries doc into 'active' | 'completed' | 'upcoming'.
 */
export function entryStatus(entry) {
  if (entry.status === 'resulted') return 'completed';
  // Check raceComplianceSummary — most reliable source of race count
  const complianceMap = entry.raceCompliancySummary || entry.raceComplianceSummary || {};
  const complianceKeys = Object.keys(complianceMap);
  const settledRaces   = complianceKeys.filter(k => complianceMap[k]?.isSettled === true).length;
  const totalFromCompliance = complianceKeys.length;

  const settled = entry.racesSettledCount || entry.racesSettled || settledRaces || 0;
  const total   = entry.totalRaces || totalFromCompliance || 0;

  if (total > 0 && settled >= total) return 'completed';
  if (entry.status === 'submitted') return 'active';
  return 'upcoming';
}

const STATUS_LABELS = { active: 'Active', completed: 'Completed', upcoming: 'Upcoming' };

/**
 * Race 1 = the lowest raceNumber in the (already raceNumber-ordered) list,
 * falling back to the first doc if raceNumber is somehow missing.
 */
export function getRaceOne(races) {
  if (!races || races.length === 0) return null;
  return races.find(r => Number(r.raceNumber) === 1) || races[0];
}

/**
 * Canonical virtual-earnings figure for a player_entries doc.
 *
 * `financialAudit.totalPrizeDividendsWon` is the ONLY field settleRaceResult.js
 * actually increments on settlement — it's the same source the live
 * game_slates/{slateId}/leaderboard subcollection and the RACE_RESULT
 * notification amounts are built from. `totalVirtualEarnings` is written
 * once at submission time (ReviewSlip.jsx, always 0) and never updated
 * afterwards, so it must never be used as a live earnings figure — it's
 * kept here only as a last-resort fallback for very old/odd data.
 *
 * DO NOT derive this from totalBankroll - remainingBankroll: that's spend,
 * not winnings.
 */
export function getEntryEarnings(entry) {
  if (!entry) return 0;
  if (entry.financialAudit && typeof entry.financialAudit.totalPrizeDividendsWon === 'number') {
    return entry.financialAudit.totalPrizeDividendsWon;
  }
  return entry.totalVirtualEarnings || 0;
}

/**
 * Builds the full set of display metrics for a single My Stable card.
 *
 * `races` is the ordered game_slates/{slateId}/races list for this entry's
 * slate (may be undefined/empty while still loading, or intentionally not
 * fetched for completed entries — their entry doc's own totalRaces/
 * racesSettledCount fields are already reliable once complete).
 *
 * @param {Object} entry - player_entries doc
 * @param {Array}  races - game_slates/{slateId}/races docs, ordered by raceNumber
 * @param {number} now   - Date.now() reference, injectable for tests
 */
export function buildStableCardMetrics(entry, races, now = Date.now()) {
  const status = entryStatus(entry);
  const virtualWinnings = getEntryEarnings(entry);
  const invested  = entry.totalSpent || 0;
  const remaining = entry.remainingBankroll || 0;

  const hasRaceData = Array.isArray(races) && races.length > 0;
  const totalRaces = hasRaceData ? races.length : (entry.totalRaces || 0);
  const resultedRaceCount = hasRaceData
    ? races.filter(r => r.status === 'resulted').length
    : (entry.racesSettledCount || entry.racesSettled || 0);

  const progressPct = totalRaces > 0
    ? Math.min(100, Math.round((resultedRaceCount / totalRaces) * 1000) / 10)
    : 0;

  const boardEnabled = status === 'completed' || resultedRaceCount > 0;

  let nextRaceLabel  = null;
  let countdownLabel = null;

  if (status === 'completed') {
    countdownLabel = 'Completed';
  } else if (hasRaceData) {
    const race1 = getRaceOne(races);
    const race1StartMs = toMs(race1?.startTimestamp ?? race1?.scheduledJump);
    const race1Jumped = race1?.status === 'locked' || race1?.status === 'resulted'
      || (race1StartMs != null && now >= race1StartMs);

    if (!race1Jumped) {
      if (race1StartMs != null) {
        const remaining = formatMinuteCountdown(race1StartMs - now);
        countdownLabel = remaining ? `Race 1 in ${remaining}` : null;
      }
    } else if (resultedRaceCount === 0) {
      countdownLabel = 'Live';
    } else {
      const nextRace = races.find(r => r.status !== 'resulted');
      if (nextRace) {
        nextRaceLabel  = `Race ${nextRace.raceNumber}`;
        countdownLabel = `${resultedRaceCount} / ${totalRaces} resulted · Race ${nextRace.raceNumber} next`;
      } else {
        countdownLabel = `${resultedRaceCount} / ${totalRaces} resulted`;
      }
    }
  }

  return {
    status,
    statusLabel: STATUS_LABELS[status] || status,
    totalRaces,
    resultedRaceCount,
    progressPct,
    virtualWinnings,
    invested,
    remaining,
    boardEnabled,
    nextRaceLabel,
    countdownLabel,
  };
}
